<template>
  <div>
    <div class="text-xl font-bold text-text leading-tight mb-2">{{ $t('listTitle') }}</div>
    <draggable :value="listing"
               class="divide-y divide-border -mx-2 lg:-mx-6"
               tag="div"
               handle=".handle"
               :group="{ name: 'groups' }"
               @input="handleGroups">
      <div v-for="(el,i) in listing" v-show="showGroup(el)" :key="i" class="pt-8 mb-6">
        <div class="flex mb-4 group px-2 pr-4 lg:px-6"
             v-if="(accounts_by_groups.length > 1
                         || (accounts_by_groups.id === null && accounts_by_groups.length === 1))
                         && !(el.id === null && el.childs.length === 0)">
          <div class="text-text text-md mr-auto font-bold">{{ el.title }}</div>
          <div v-if="el.id!==null && !currentUserIsWatcher"
               @click="$emit('editGroup',el)">
            <IconEdit class="opacity-0 group-hover:opacity-100
                             transition duration-200 cursor-pointer
                             text-icon hover:text-icon-hover mr-4"/>
          </div>
          <div v-if="(el.id!==null && listing.length > 2) && !currentUserIsWatcher"
               class="control-icon handle lg:-mr-2">
            <IconMove class="transition duration-200 cursor-pointer
                               text-icon hover:text-icon-hover"/>
          </div>
        </div>
        <draggable
            :value="el.childs"
            handle=".handle"
            :group="{ name: 'items' }"
            :disabled="space_sort !== 1"
            @input="handleAccounts($event,el.id)">
          <div v-if="el.childs.length > 0" v-for="(item,j) in el.childs" :key="j"
               class="flex items-center rounded pl-4 lg:pl-11 pr-4 py-4
                        bg-block hover:bg-bg group
                        transition duration-200"
               :class="{'even:bg-bg':listing.length === 1}">
            <MKIcons :icon="item.icon" class="text-icon mr-6 w-10 h-10 -my-2 text-md"
                     :style="accountIconStyle(item)"/>
            <div class="text-text-secondary text-sm mr-auto -ml-0-5">{{ item.title }}</div>
            <div class="mr-16 flex items-center space-x-4">
              <IconEyeCross v-if="!!item.is_hidden"
                            class="text-icon-info"
                            v-tooltip="$t('tooltipIsHidden')"/>
              <IconCapitalizationCross v-if="!item.capitalization"
                                       class="text-icon-info"
                                       v-tooltip="$t('tooltipIsCapitalization')"/>
              <IconFolderCross v-if="!item.group_capitalization"
                               class="text-icon-info"
                               v-tooltip="$t('tooltipIsGroupAccounting')"/>
              <IconPercent v-if="!!item.commission"
                           class="text-icon-info"
                           v-tooltip="$t('tooltipCommission',{commission:commissionTooltip(item)})"/>
            </div>
            <div class="mr-4"
                 v-if="!currentUserIsWatcher"
                 @click="$emit('edit',item)">
              <IconEdit class="transition duration-200 cursor-pointer
                               opacity-0 group-hover:opacity-100
                               text-icon hover:text-icon-hover"/>
            </div>
            <template v-if="!currentUserIsWatcher">
              <div class="handle" v-if="space_sort === 1">
                <IconMove class="transition duration-200 cursor-pointer text-icon hover:text-icon-hover"/>
              </div>
              <div v-else>
                <IconSort class="text-icon"/>
              </div>
            </template>
          </div>
          <div slot="footer" v-if="el.childs.length === 0 && (el.id !== null || listing.length === 1) "
               class="text-md pl-6 text-text">
            {{ $t('groupEmpty') }}
          </div>
        </draggable>
      </div>
    </draggable>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import draggable from 'vuedraggable';
import IconEdit from "@/components/icons/IconEdit";
import IconMove from "@/components/icons/IconMove";
import IconSort from "@/components/icons/IconSort";
import IconPercent from "@/components/icons/IconPercent";
import IconCapitalizationCross from "@/components/icons/IconCapitalizationCross";
import IconFolderCross from "@/components/icons/IconFolderCross";
import IconEyeCross from "@/components/icons/IconEyeCross";
import MKIcons from "@/components/icons/MKIcons/MKIcons";

export default {
  name: "AccountsList",
  components: {
    MKIcons, IconEyeCross, IconFolderCross, IconCapitalizationCross, IconPercent, IconSort, IconMove, IconEdit,
    draggable,
  },
  props: {
    space_sort: {
      type: Number,
      default: 2
    }
  },
  data() {
    return {
      listing: [],
    }
  },
  computed: {
    ...mapState('space', ['current_space']),
    ...mapState('space/accounts', ['accounts', 'account_groups']),
    ...mapGetters('space/accounts', ['accounts_by_groups']),
    ...mapGetters('space', ['currentUserIsWatcher'])
  },
  watch: {
    space_sort() {
      this.listing = this.accounts_by_groups;
    }
  },
  methods: {
    ...mapActions('space/accounts', ['updateAccountsOrder', 'updateAccountGroupsOrder']),
    setListing() {
      this.listing = this.accounts_by_groups;
    },
    showGroup(group) {
      return !(group.id === null && (!group.childs || group.childs.length === 0));
    },
    accountIconStyle(item) {
      let style = '';
      if (item.icon_color) style += `color:${item.icon_color};`;
      if (item.icon_bg) style += `background:${item.icon_bg};`;
      return style;
    },
    handleGroups(groups) {
      this.listing = groups.map((item, i) => ({...item, order: !!item.id ? i : 999}));
      //- sort null group as the last
      this.listing.sort((a, b) => a.order - b.order);

      this.updateAccountGroupsOrder(this.listing);
    },
    handleAccounts(list, group_id) {
      //list - accounts in group, id - group id of list
      //apply changes in full list
      this.listing = this.listing.map(i => {
        if (i.id === group_id) {
          return {
            ...i,
            childs: list.map((j, o) => ({...j, group_id: group_id, order: o}))
          };
        } else {
          return i
        }
      });

      this.updateAccountsOrder({list, group_id})
    },
    commissionTooltip(account) {
      return `${account.commission_value} ${account.commission_type === 1 ? '%' : account.currency.symbol}`
    }
  },
  mounted() {
    this.setListing();
  },
  i18n: {
    messages: {
      en: {
        listTitle: 'Your accounts list',
        groupEmpty: 'Empty group',
        tooltipCommission: 'Commission - {commission}',
        tooltipIsHidden: 'Hidden in list of accounts',
        tooltipIsCapitalization: 'Not included in total capital',
        tooltipIsGroupAccounting: 'Not included in group balance'
      },
      ru: {
        listTitle: 'Список ваших счетов',
        groupEmpty: 'В этой группе пока нет счетов',
        tooltipCommission: 'Комиссия - {commission}',
        tooltipIsHidden: 'Скрыт в списке счетов',
        tooltipIsCapitalization: 'Баланс не учитывается в общем капитале',
        tooltipIsGroupAccounting: 'Баланс не учитывается в группе'
      }
    }
  }
}
</script>
